import { isIElementAttachmentsGroup, isIElementMarkupImage, isIElementMarkupWithTypeHint } from "@custom-types/i-elements-type-guards";
import { IElementTypeHint, isIElementAreaSection, isIElementImg360, isIElementMarkupAssignee, isIElementMarkupDueTime, isIElementMarkupState, isIElementMeasurePolygon, isIElementModel3d, isIElementPointCloudStream, isIElementSection } from "@faro-lotv/ielement-types";
import { selectAllIElementsOfType, selectAncestor, selectChildDepthFirst, selectChildrenDepthFirst, selectIElement, selectIElementAncestors } from "@faro-lotv/project-source";
import { selectRootExternalId } from "@store/i-elements/i-elements-selectors";
import { selectedProjectIdSelector } from "@store/projects/projects-selector";
import { slideNodesSelector } from "@store/slide-nodes/slide-nodes-selector";
import { findSlideNodeAndSceneObject, sortMarkups } from "@utils/markups-utils";
import { getSphereViewerUrl } from "@utils/project-utils";
import { getMarkupLocationSections } from "@utils/markup-location-utils";
import { getSceneObjectWebEditorUrl } from "@utils/web-editor-utils";

/**
 * @returns All Markup entities generated from the ielements available in the redux store.
 * @param state Redux store state.
 */
export function getMarkups(state) {
  const markups = selectAllIElementsOfType(isIElementMarkupWithTypeHint)(state);
  const extendedMarkups = markups.map(markup => getMarkup(state, markup));
  return sortMarkups(extendedMarkups);
}

/**
 * @returns A Markup entity from a given markup ielement.
 * @param state Redux store state.
 * @param markup Markup ielement.
 */
function getMarkup(state, markup) {
  return {
    ...markup,
    assignee: getAssignee(state, markup),
    dueDate: getDueDate(state, markup),
    status: getStatus(state, markup),
    image: getImage(state, markup),
    model3d: getModel3d(state, markup),
    section: getSection(state, markup),
    areaSection: getAreaSection(state, markup),
    sphereViewerUrl: getMarkupSphereViewerUrl(state, markup),
    webEditorUrl: getMarkupWebEditorUrl(state, markup),
    attachmentsCount: getAttachmentsCount(state, markup),
    measurePolygon: getMeasurePolygon(state, markup)
  };
}

/** Gets the markup assignee element associated to the markup */
function getAssignee(state, markup) {
  return selectChildDepthFirst(markup, isIElementMarkupAssignee)(state);
}

/** Gets the markup dueDate element associated to the markup */
function getDueDate(state, markup) {
  return selectChildDepthFirst(markup, isIElementMarkupDueTime)(state);
}

/** Gets the markup status element associated to the markup */
function getStatus(state, markup) {
  return selectChildDepthFirst(markup, isIElementMarkupState)(state);
}

/** Gets the 2d image element associated to the markup */
function getImage(state, markup) {
  return selectChildDepthFirst(markup, isIElementMarkupImage)(state);
}

/** Gets the Model3d element associated to the markup */
function getModel3d(state, markup) {
  return selectAncestor(markup, isIElementModel3d)(state);
}

/** Gets the section element associated to the markup */
function getSection(state, markup) {
  return selectAncestor(markup, isIElementSection)(state);
}

/** Gets the area section element associated to the markup */
function getAreaSection(state, markup) {
  return selectAncestor(markup, isIElementAreaSection)(state);
}

/** Whether the Sphere Viewer can open the element: a point cloud or a panorama */
function isViewerData(element) {
  return isIElementPointCloudStream(element) || isIElementImg360(element);
}

/**
 * @returns the URL to open the place in the Sphere Viewer looking at the markup, through the
 * point cloud or panorama it is, or has, e.g. the scan's one or the merged one of a data session.
 * A place without any, e.g. a cluster, opens the project without focusing anything.
 */
function selectPlaceViewerUrl(place, markupId) {
  return state => {
    const projectId = selectRootExternalId(state);
    const data = isViewerData(place) ? place : place.childrenIds?.map(id => selectIElement(id)(state)).find(child => child !== undefined && isViewerData(child));
    if (!projectId) {
      return;
    }
    if (!data) {
      return getSphereViewerUrl({
        projectId,
        lookAtId: place.id
      }).href;
    }

    // Like the link that opens the markup from its preview
    return getSphereViewerUrl({
      projectId,
      id: data.id,
      lookAtId: markupId,
      additionalParams: new URLSearchParams({
        mode: "walk",
        scene: isIElementImg360(data) ? "360" : "pc"
      })
    }).href;
  };
}

/**
 * @returns where the element is, from the outermost place to the element itself when it is one,
 * as the Sphere Viewer names them, with a link to open each in it looking at the markup
 * @param elementId ID of the element, e.g. a markup or the point cloud it is previewed in
 * @param markupId ID of the markup, for the viewer to look at
 */
export function selectLocationPath(elementId, markupId) {
  return state => {
    const element = selectIElement(elementId)(state);
    return getMarkupLocationSections({
      ancestors: [...(element ? [element] : []), ...selectIElementAncestors(elementId)(state).map(id => selectIElement(id)(state)).filter(ancestor => ancestor !== undefined)],
      getSphereViewerUrl: place => selectPlaceViewerUrl(place, markupId)(state)
    });
  };
}

/** Gets the WebEditor URL of the markup */
function getMarkupWebEditorUrl(state, markup) {
  // Find the slide node and the scene object associated to the markup
  const slideNodes = slideNodesSelector(state);
  const model3d = getModel3d(state, markup);
  const slideNodeAndSceneObject = findSlideNodeAndSceneObject(slideNodes, markup, model3d);
  const projectId = selectedProjectIdSelector(state);
  if (!slideNodeAndSceneObject || !projectId) {
    return;
  }
  return getSceneObjectWebEditorUrl({
    projectId,
    ...slideNodeAndSceneObject
  });
}

/** Gets the Sphere Viewer URL of the markup */
function getMarkupSphereViewerUrl(state, markup) {
  const projectId = selectRootExternalId(state);
  const lookAtId = markup.id;
  if (!projectId) {
    return;
  }
  return getSphereViewerUrl({
    projectId,
    lookAtId
  }).href;
}

/**
 * @returns the number of attachments of the provided markup
 * The amount of attachments is taken from the sum of children IDs of each the attachments group element.
 * @param state Redux store state
 * @param markup Markup element
 */
function getAttachmentsCount(state, markup) {
  const attachmentsGroups = selectChildrenDepthFirst(markup, isIElementAttachmentsGroup)(state);
  return attachmentsGroups.reduce((acc, el) => acc + (el.childrenIds?.length ?? 0), 0);
}

/** Gets the parent measure polygon element associated to the markup */
function getMeasurePolygon(state, markup) {
  return selectAncestor(markup, isIElementMeasurePolygon)(state);
}

/**
 * Determines if a 3D model node should be deleted based on its children and type.
 */
export function shouldDeleteModel3D(model3d, elementId) {
  if (!model3d || model3d.typeHint !== IElementTypeHint.node) {
    return false;
  }
  const otherChildren = model3d.childrenIds?.filter(id => id !== elementId) || [];
  return otherChildren.length === 0;
}